/**
 * @file Sauvegarde cloud cote application : arbitrage au demarrage (avant la creation de la Simulation), envoi differe des sauvegardes locales,
 * adoption d'une version distante plus recente (rechargement), reinitialisation / import qui ecrasent le serveur.
 */
import type { Locale } from '@shared/enums';
import { CloudSync, toRemoteSave } from '@net/CloudSync';
import type { PushOutcome } from '@net/CloudSync';
import type { WallClock } from '@core/index';
import type { LoadOutcome, SaveManager } from './SaveManager';
import type { OnlineService } from './OnlineService';

/** Frequence de verification des envois en attente (ms) ; le delai minimal entre deux envois est dans CloudSync. */
const TICK_MS = 5000;

export interface BootSyncResult {
  loaded: LoadOutcome;
  /** Cle i18n d'une notification a afficher une fois le jeu demarre. */
  toast?: string;
}

export class CloudBridge {
  private cs: CloudSync | null = null;
  private timer: number | null = null;
  private reloading = false;

  constructor(
    private readonly online: OnlineService,
    private readonly saves: SaveManager,
    private readonly clock: WallClock,
    private readonly reload: () => void = () => window.location.reload(),
  ) {}

  get active(): boolean {
    return this.cs !== null;
  }

  /** A appeler avant de construire la Simulation : connecte, arbitre, et retourne la partie a jouer. Ne leve jamais (repli hors ligne). */
  async syncOnBoot(loaded: LoadOutcome, locale: Locale): Promise<BootSyncResult> {
    if (!this.online.enabled) return { loaded };
    try {
      if (!(await this.online.connect()) || !this.online.sync || !this.online.userId) return { loaded };
      const sync = this.online.sync;
      const cs = await CloudSync.create(sync, this.saves.kv, this.online.userId);
      const isNew = loaded.source === 'new';
      const raw = isNew ? null : await this.saves.readRaw();
      const decision = await cs.reconcile(raw ? toRemoteSave(raw) : null, isNew);
      this.cs = cs;
      if (decision.choice === 'remote' && decision.remote) {
        const adopted = await this.saves.parseRemote(decision.remote.payload, locale);
        if (adopted) {
          // l'ancienne version locale (si elle existait) est conservee comme copie de secours
          await this.saves.replaceWithRemote(decision.remote.payload);
          return { loaded: adopted, ...(decision.reason === 'adopted' ? { toast: 'toast.cloudLoaded' } : {}) };
        }
        // version distante illisible : on garde la locale et on la pousse d'office
        await cs.markForceNext();
      }
      return { loaded };
    } catch (err) {
      console.warn('synchronisation cloud impossible au demarrage', err);
      this.cs = null;
      return { loaded };
    }
  }

  /** Demarre la surveillance : chaque ecriture locale marque la synchro « a envoyer », un minuteur envoie quand le delai est ecoule. */
  start(): () => void {
    if (!this.cs) return () => undefined;
    this.saves.onSaved = () => this.cs?.markDirty();
    this.timer = window.setInterval(() => void this.flush(false), TICK_MS);
    return () => {
      this.saves.onSaved = null;
      if (this.timer !== null) window.clearInterval(this.timer);
      this.timer = null;
    };
  }

  /** Envoie si necessaire (`force` : sans attendre le delai, ex. mise en arriere-plan). */
  async flush(force: boolean): Promise<PushOutcome | null> {
    const cs = this.cs;
    if (!cs || this.reloading) return null;
    const out = await cs.pushIfDue(this.clock.nowMs(), () => this.saves.readRaw(), force);
    if (out?.kind === 'remote-wins') {
      // un autre appareil a joue plus recemment : on adopte sa version et on recharge
      this.reloading = true;
      await this.saves.replaceWithRemote(out.remote.payload, true);
      this.reload();
    } else if (out?.kind === 'failed') {
      console.warn('envoi de la sauvegarde cloud echoue (nouvel essai plus tard)', out.error);
    }
    return out;
  }

  /** Reinitialisation / import : la prochaine version locale ecrasera celle du serveur. */
  async markOverwrite(): Promise<void> {
    if (this.cs) await this.cs.markForceNext();
    else if (this.online.enabled) await CloudSync.markForceNextOffline(this.saves.kv);
  }
}
